import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import { ConfigurationSection } from '../../../../containers/ConfigurationSection';
import type {
  XeroConfigurationControl,
  XeroConfigurationFormValues,
} from '../../XeroConfigurationFormSchema';
import { useXeroDepositToAccounts } from '../../hooks/useXeroDepositToAccounts';
import { VyAutocompleteSelect } from '../../../../../../../../components/new-design/inputs/VyAutocompleteSelect';

type SelectOption = {
  label: string;
  value: string;
};

type PaymentsConfigurationSectionProps = {
  control: XeroConfigurationControl;
  defaultValues: XeroConfigurationFormValues;
};

const filterByLabel = (option: SelectOption, query: string) =>
  (option?.label ?? '').toLowerCase().includes(query.toLowerCase());

const getLabel = (option: SelectOption) => option?.label ?? '';

export const PaymentsConfigurationSection: FC<PaymentsConfigurationSectionProps> = ({
  control,
  defaultValues,
}) => {
  const { data: depositToAccounts } = useXeroDepositToAccounts();

  const depositToOptions: SelectOption[] = (depositToAccounts ?? []).map((account) => ({
    label: account.name,
    value: account.id,
  }));

  const handleOptionSelect = (field: { onChange: (value: string | null) => void }) =>
    (option: SelectOption | string | null) => {
      if (option === null) {
        field.onChange(null);
        return;
      }

      const value = typeof option === 'string' ? option : option.value;
      field.onChange(value);
    };

  return (
    <ConfigurationSection title="Payments" iconName="CreditCard">
      <Controller
        name="defaultStripeDepositToAccountId"
        control={control}
        defaultValue={defaultValues.defaultStripeDepositToAccountId}
        render={({ field }) => (
          <VyAutocompleteSelect
            options={depositToOptions}
            fullWidth
            onOptionSelect={handleOptionSelect(field)}
            filterOption={filterByLabel}
            getOptionLabel={getLabel}
            label="Default deposit-to account for Stripe payments"
            value={depositToOptions.find((option) => option.value === field.value) ?? undefined}
          />
        )}
      />
      <Controller
        name="defaultDepositToAccountId"
        control={control}
        defaultValue={defaultValues.defaultDepositToAccountId}
        render={({ field }) => (
          <VyAutocompleteSelect
            options={depositToOptions}
            fullWidth
            onOptionSelect={handleOptionSelect(field)}
            filterOption={filterByLabel}
            getOptionLabel={getLabel}
            label="Default deposit-to account for payments"
            value={depositToOptions.find((option) => option.value === field.value) ?? undefined}
          />
        )}
      />
    </ConfigurationSection>
  );
};
